import { memo } from 'react';
import { ChevronRight } from 'lucide-react';
import type { ArbolComposicion } from '../../lib/composicion';

/**
 * Arco del método sobre el lienzo (pedagogía del compositor): franja compacta
 * con Empatizar→Definir→Idear→Prototipar→Testear. Cada familia se «enciende»
 * cuando el proceso ya tiene actividades de esa etapa, así el facilitador ve
 * de un vistazo la silueta metodológica de lo que está armando. Es solo
 * lectura: no bloquea nada ni agrega pasos al flujo por clic.
 */

interface FamiliaArco {
  clave: string;
  etiqueta: string;
  /** UNA línea, en español llano, de qué hace esa etapa en el método. */
  linea: string;
}

const FAMILIAS_ARCO: FamiliaArco[] = [
  {
    clave: 'empatizar',
    etiqueta: 'Empatizar',
    linea: 'Escuchar a la gente y entender su realidad antes de proponer nada.',
  },
  {
    clave: 'definir',
    etiqueta: 'Definir',
    linea: 'Aterrizar lo escuchado en un problema claro que todos compartan.',
  },
  {
    clave: 'idear',
    etiqueta: 'Idear',
    linea: 'Sacar muchas ideas posibles, sin descartar ninguna todavía.',
  },
  {
    clave: 'prototipar',
    etiqueta: 'Prototipar',
    linea: 'Volver las mejores ideas algo concreto que se pueda mostrar.',
  },
  {
    clave: 'testear',
    etiqueta: 'Testear',
    linea: 'Poner lo propuesto frente a la gente y aprender de su reacción.',
  },
];

/**
 * Helper PURO: cuenta las actividades del árbol por familia de herramienta
 * (clave normalizada en minúsculas). Las familias fuera del arco también se
 * cuentan; el componente solo pinta las cinco del método.
 */
export function contarFamilias(arbol: ArbolComposicion): Record<string, number> {
  const conteo: Record<string, number> = {};
  for (const fase of arbol.fases) {
    for (const sesion of fase.sesiones) {
      for (const actividad of sesion.actividades) {
        const clave = (actividad.herramienta.familia ?? '').trim().toLowerCase();
        if (!clave) continue;
        conteo[clave] = (conteo[clave] ?? 0) + 1;
      }
    }
  }
  return conteo;
}

export const ArcoFamilias = memo(function ArcoFamilias({
  conteo,
}: {
  /** Resumen {familia: cantidad de actividades} calculado con {@link contarFamilias}. */
  conteo: Record<string, number>;
}) {
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-border px-6 py-2">
      <span className="font-display text-xs font-semibold uppercase tracking-wide text-content-muted">
        Método
      </span>
      <ol
        aria-label="Arco del método: Empatizar, Definir, Idear, Prototipar y Testear"
        className="flex flex-wrap items-center gap-x-1.5 gap-y-1"
      >
        {FAMILIAS_ARCO.map((familia, i) => {
          const cantidad = conteo[familia.clave] ?? 0;
          const cubierta = cantidad > 0;
          return (
            <li key={familia.clave} className="flex items-center gap-1.5">
              {i > 0 && (
                <ChevronRight
                  className="h-3 w-3 shrink-0 text-content-muted"
                  aria-hidden="true"
                />
              )}
              <span
                title={familia.linea}
                className={`flex items-center gap-1.5 font-sans text-xs ${
                  cubierta ? 'font-medium text-content' : 'text-content-muted'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`h-2 w-2 shrink-0 rounded-full ${
                    cubierta ? 'bg-current' : 'border border-current'
                  }`}
                />
                {familia.etiqueta}
                {cubierta && (
                  <span className="tabular-nums text-content-muted">{cantidad}</span>
                )}
                <span className="sr-only">
                  {cubierta
                    ? `: ${cantidad} ${cantidad === 1 ? 'actividad' : 'actividades'} en el proceso.`
                    : ': sin actividades todavía.'}
                </span>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
});
